import React, { useState, useEffect } from 'react';
import { getSourceHealth } from '../services/api';
import { 
  Database, ExternalLink, CheckCircle2, AlertTriangle, XCircle, 
  Activity, Globe, Pill, Shield, Clock, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';

const STATUS_CONFIG = {
  current: { color: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Current', Icon: CheckCircle2 },
  warning: { color: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Needs Update', Icon: AlertTriangle },
  stale:   { color: 'bg-red-500', ring: 'ring-red-200', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Stale', Icon: XCircle },
};

function HealthRing({ percent }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = percent >= 80 ? '#10b981' : percent >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative w-36 h-36" data-testid="health-ring">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="8" />
        <circle cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black text-slate-900">{percent}%</span>
        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Current</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

function SourceRow({ source, expanded, onToggle }) {
  const cfg = STATUS_CONFIG[source.status] || STATUS_CONFIG.stale;
  const StatusIcon = cfg.Icon;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? 'border-slate-300 shadow-sm' : 'border-slate-200'}`} data-testid={`source-row-${source.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-4 hover:bg-slate-50/50 transition-colors text-left">
        {/* Status dot */}
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${cfg.color} ring-4 ${cfg.ring}`} />

        {/* Name & authority */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900 truncate">{source.name}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} ${cfg.border} border`}>
              <StatusIcon className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />
              {cfg.label}
            </span>
            {source.reliability === 'high' && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">High Reliability</span>
            )}
          </div>
          <p className="text-xs text-slate-400 truncate">{source.authority}</p>
        </div>

        {/* Metrics */}
        <div className="hidden md:flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <p className="text-sm font-bold text-slate-800">{source.drug_count}</p>
            <p className="text-[10px] text-slate-400">Drugs</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-slate-800">{source.generic_count}</p>
            <p className="text-[10px] text-slate-400">Generics</p>
          </div>
          <div className="text-center w-20">
            <p className="text-sm font-bold text-slate-800">{source.days_since_update != null ? `${source.days_since_update}d` : '—'}</p>
            <p className="text-[10px] text-slate-400">Since Update</p>
          </div>
        </div>

        {/* Region badge */}
        <span className="hidden lg:inline text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">{source.region}</span>

        {/* Expand icon */}
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
            {/* Details */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Full Name</p>
              <p className="text-xs text-slate-700 font-medium">{source.full_name}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-3 mb-1">Type</p>
              <p className="text-xs text-slate-700 capitalize">{source.type}</p>
              {source.url && (
                <a href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2">
                  Visit Source <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Coverage */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Regions Covered</p>
              <div className="flex flex-wrap gap-1">
                {source.regions_covered.map(r => (
                  <span key={r} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-white text-slate-600 border border-slate-200">{r}</span>
                ))}
                {source.regions_covered.length === 0 && <span className="text-[10px] text-slate-400">—</span>}
              </div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-3 mb-1">Therapeutic Areas</p>
              <div className="flex flex-wrap gap-1">
                {source.therapeutic_areas.map(ta => (
                  <span key={ta} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-white text-slate-600 border border-slate-200">{ta}</span>
                ))}
                {source.therapeutic_areas.length === 0 && <span className="text-[10px] text-slate-400">—</span>}
              </div>
            </div>

            {/* Drugs backed */}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Drugs Backed ({source.drug_count})</p>
              <div className="flex flex-wrap gap-1">
                {source.drug_names.slice(0, 8).map(name => (
                  <span key={name} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-white text-slate-700 border border-slate-200">{name}</span>
                ))}
                {source.drug_names.length > 8 && <span className="text-[10px] text-slate-400">+{source.drug_names.length - 8} more</span>}
              </div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-3 mb-1">Last Verified</p>
              <p className="text-xs text-slate-700 font-mono">{source.last_verified || 'Never'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SourceHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    async function fetchHealth() {
      try {
        const res = await getSourceHealth();
        setData(res.data);
      } catch (err) {
        console.error('Failed to load source health:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchHealth();
  }, []);

  if (loading) {
    return (
      <div className="p-6" data-testid="source-health-loading">
        <div className="skeleton h-8 w-64 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
        </div>
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-center text-slate-500">Failed to load source health data</div>;
  }

  const { overall_health, sources, coverage_summary, stale_threshold_days } = data;

  const filteredSources = filter === 'all' ? sources : sources.filter(s => s.status === filter);
  const staleAlerts = sources.filter(s => s.status === 'stale');

  return (
    <div className="p-6" data-testid="source-health-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Source Health Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor the freshness and coverage of regulatory data sources</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Clock className="w-3.5 h-3.5" />
          <span>As of {overall_health.as_of}</span>
        </div>
      </div>

      {/* Stale Alerts */}
      {staleAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3 mb-6" data-testid="stale-alerts-banner">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">{staleAlerts.length} source{staleAlerts.length > 1 ? 's' : ''} exceeding {stale_threshold_days}-day freshness threshold</p>
            <p className="text-xs text-red-600 mt-0.5">
              {staleAlerts.map(s => s.name).join(', ')} — data may be outdated. Contact data team for re-verification.
            </p>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-center">
          <HealthRing percent={overall_health.score_percent} />
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Sources" value={overall_health.total_sources} sub="Regulatory & patent databases" Icon={Database} accent="bg-[#0A2540]" />
          <StatCard label="Current" value={overall_health.current} sub={`Updated within ${data.warning_threshold_days} days`} Icon={CheckCircle2} accent="bg-emerald-500" />
          <StatCard label="Needs Update" value={overall_health.warning} sub={`${data.warning_threshold_days}-${stale_threshold_days} days old`} Icon={AlertTriangle} accent="bg-amber-500" />
          <StatCard label="Stale" value={overall_health.stale} sub={`>${stale_threshold_days} days without verification`} Icon={XCircle} accent="bg-red-500" />
        </div>
      </div>

      {/* Coverage Matrix */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="coverage-matrix">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-[#0A2540]" />
          <h2 className="text-sm font-bold text-slate-900">Coverage Matrix</h2>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Pill className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600"><strong>{coverage_summary.total_drugs}</strong> drugs tracked</span>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600"><strong>{coverage_summary.total_generics}</strong> generics tracked</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex flex-wrap gap-1.5">
            {coverage_summary.regions.map(r => (
              <span key={r} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{r}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { key: 'all', label: `All (${sources.length})` },
          { key: 'current', label: `Current (${overall_health.current})` },
          { key: 'warning', label: `Warning (${overall_health.warning})` },
          { key: 'stale', label: `Stale (${overall_health.stale})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${filter === tab.key ? 'bg-[#0A2540] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
            data-testid={`filter-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Source List */}
      <div className="space-y-2" data-testid="source-list">
        {filteredSources.map(source => (
          <SourceRow key={source.id} source={source} expanded={expandedId === source.id}
            onToggle={() => setExpandedId(expandedId === source.id ? null : source.id)} />
        ))}
        {filteredSources.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <Shield className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No sources match the selected filter</p>
          </div>
        )}
      </div>

      {/* Footer Disclaimer */}
      <div className="mt-8 text-center">
        <p className="text-[10px] text-slate-400">
          Source health is calculated based on last_verified dates in drug and generic regulatory entries.
          Staleness threshold: {stale_threshold_days} days. Data freshness does not imply regulatory validity — always verify with the authoritative source.
        </p>
      </div>
    </div>
  );
}
